import { Component, Ref } from "@eastsideco/salvo-ts";
import { ThemeComponent } from '@/theme/ThemeComponent';
import { elevate, type Autocomplete, type PhraseSuggestion, type ProductGroup } from '@apptus/esales-api';
import { debounce } from "lodash";
import { formatMoney } from "@/utils/formatting";
import { Shopify } from "@/shopify-types";
import {
    getLocalisedElevate,
    resetElevateSessionIfTrackingConsentDenied
} from "@/utils/elevate";
import { SimpleEvent } from "@eastsideco/salvo-ts";

declare global {
    interface Window {
        elevate: ReturnType<typeof elevate>;
    }
}

@Component
export class VoyadoSearchForm extends ThemeComponent  {
    @Ref searchForm!: HTMLInputElement;
    @Ref searchSubmit!: HTMLButtonElement;
    @Ref searchButton!: HTMLButtonElement;
    @Ref clearButton!: HTMLButtonElement;

    @Ref searchResultsContainer!: HTMLElement;

    @Ref phraseSuggestionsContainer!: HTMLDivElement;
    @Ref phraseSuggestions!: HTMLDivElement;
    @Ref productsContainer!: HTMLDivElement;
    @Ref products!: HTMLDivElement;

    @Ref searchCollectionsContainer!: HTMLDivElement;
    @Ref searchPagesContainer!: HTMLDivElement;
    @Ref searchArticlesContainer!: HTMLDivElement;
    @Ref searchCollectionsContent!: HTMLDivElement;
    @Ref searchPagesContent!: HTMLDivElement;
    @Ref searchArticlesContent!: HTMLDivElement;

    private result?: Autocomplete;
    public term: string = '';
    private _onSearchChange = new SimpleEvent<Autocomplete | undefined>();
    private _onShopifyCollectionsLoaded = new SimpleEvent<Shopify.Liquid.Collection[]>();

    get searchPerformed() {
        return this.result != undefined;
    }

    get hasResults() {
        if (!this.result) {
            return false;
        }

        return this.result?.productSuggestions.length > 0;
    }

    public get onSearchChange() {
        return this._onSearchChange.asEvent();
    }

    public get onShopifyCollectionsLoaded() {
        return this._onShopifyCollectionsLoaded.asEvent();
    }

    init() {
        this.setResultsVisibility(false);

        this.$listen(this.searchForm, 'keydown', debounce((event: KeyboardEvent) => {
            if (event.code === 'Escape') {
                this.searchForm.value = '';
                this.setResultsVisibility(false);
                return;
            }

            if (event?.target == null) {
                return;
            }

            const target = event.target as HTMLInputElement;

            void this.onSearchInput(target.value);
        }, 50));

        this.$listen(this.searchSubmit, 'click', () => {
            window.location.href = `/search?q=${encodeURI(this.searchForm.value)}`;
        });

        this.$listen(this.searchButton, 'click', () => {
            window.location.href = `/search?q=${encodeURI(this.searchForm.value)}`;
        });

        this.$listen(this.clearButton, 'click', () => {
            this.clear();
        });
    }

    clear() {
        this.searchForm.value = '';
        void this.onSearchInput('');
    }

    setResultsPosition() {
        const rect = this.getBoundingClientRect();
        this.style.setProperty('--position-x', `${rect.x}px`);
    }

    async onSearchInput(q: string) {
        if (!window.elevate) {
            return;
        }

        this.setResultsPosition();
        this.term = q;

        if (q === "") {
            this.result = undefined;

            //this.setResultsVisibility(false);

            this._onSearchChange.dispatch(undefined);

            return;
        }

        resetElevateSessionIfTrackingConsentDenied();

        const result = await getLocalisedElevate().query.autocomplete({ q }, { contentLists: [
            {
                id: "with-filtering",
                contentFilter: {
                    "custom.send_to_elevate": "true" // eslint-disable-line
                }
            }
        ] });

        this.result = result;

        this._onSearchChange.dispatch(result);
    }

    async getShopifySearchResults(q: string) {
        const params = `?q=${q}&resources[type]=page,article,collection&resources[limit]=3&resources[limit_scope]=each`;
        const response = await fetch(`/search/suggest.json${params}`);

        const data = await response.json();

        this._onShopifyCollectionsLoaded.dispatch(data.resources.results.collections);

        // this.showArticles(data.resources.results.articles);
        // this.showPages(data.resources.results.pages);
        // this.showCollections(data.resources.results.collections);
    }

    showArticles(articles: Shopify.Liquid.Article[]) {
        const articlesHTML = articles.map((article) => `
                <li>
                    <a href="${article.url}">
                        ${article.title}
                    </a>
                </li>
            `).join('');

        if (!articlesHTML || articlesHTML == '') {
            this.searchArticlesContainer.classList.add('voyado-search-form__results__container--hidden');
            return;
        }

        this.searchArticlesContainer.classList.remove('voyado-search-form__results__container--hidden');
        this.searchArticlesContent.innerHTML = `<ul>${articlesHTML}</ul>`;
    }

    showPages(pages: Shopify.Liquid.Page[]) {
        const pagesHTML = pages.map((page) => `
                <li>
                    <a href="${page.url}">
                        ${page.title}
                    </a>
                </li>
            `).join('');

        if (!pagesHTML) {
            this.searchPagesContainer.classList.add('voyado-search-form__results__container--hidden');
            return;
        }

        this.searchPagesContainer.classList.remove('voyado-search-form__results__container--hidden');
        this.searchPagesContent.innerHTML = `<ul>${pagesHTML}</ul>`;
    }

    showCollections(collections: Shopify.Liquid.Collection[]) {
        const collectionsHTML = collections.map((collection) => `
                <li>
                    <a href="${collection.url}">
                        ${collection.title}
                    </a>
                </li>
            `).join('');

        if (!collectionsHTML) {
            this.searchCollectionsContainer.classList.add('voyado-search-form__results__container--hidden');
            return;
        }

        this.searchCollectionsContainer.classList.remove('voyado-search-form__results__container--hidden');
        this.searchCollectionsContent.innerHTML = `<ul>${collectionsHTML}</ul>`;
    }


    setResultsVisibility(visible: boolean) {
        this.searchResultsContainer.classList.toggle('voyado-search-form__results--hidden', !visible);
    }

    showProducts() {
        if (! this.hasResults) {
            this.products.innerHTML = `
              <div class="voyado-search-form__no-results">
                Sorry, no products found for <strong>${this.term}</strong>
              </div>
            `;
            this.productsContainer.classList.remove('voyado-search-form__results__container--hidden');

            return;
        }

        const productsHTML = this.result?.productSuggestions.map((productGroup: ProductGroup) => {
            const [product] = productGroup.products;
            const isOnSale = product.sellingPrice.min != product.listPrice.min;

            const salePriceHTML = isOnSale ?
                `
                  <del class="voyado-search-form__product__original-price">
                    ${formatMoney(product.listPrice.min * 100)}
                  </del>
                ` :
                '';

            return `
                <li class="voyado-search-form__product">
                    <a href="${product.link}" class="flex" data-ticket="${product.ticket}">
                        <div>
                            <img src="${product.imageInfo.thumbnail}" class="voyado-search-form__product__image" />
                        </div>

                        <div class="voyado-search-form__product__title">
                            <h3 class="voyado-search-form__product__title">${product.title}</h3>

                            <div class="voyado-search-form__product__prices">
                                <div 
                                    class="
                                        voyado-search-form__product__price 
                                        ${isOnSale ? 'search-form__product__price--on-sale' : ''}
                                    "
                                >
                                    ${formatMoney(product.sellingPrice.min * 100)}
                                </div>

                                ${salePriceHTML}
                            </div>
                        </div>
                    </a>
                </li>
            `;
        }).join('');

        if (! productsHTML) {
            this.productsContainer.classList.add('voyado-search-form__results__container--hidden');
            return;
        }

        this.products.innerHTML = `<ul>${productsHTML}</ul>`;
        this.productsContainer.classList.remove('voyado-search-form__results__container--hidden');

        this.addProductClickListners();
    }

    showPhraseSuggestions() {
        const suggestionsHTML = this.result?.phraseSuggestions.map(({ q, highlighted }: PhraseSuggestion) => `
                <li>
                    <a href="/search?q=${encodeURI(q)}&origin=PHRASE_SUGGEST">
                        ${highlighted.replace(/\{/g, '<span>').replace(/\}/g, '</span>')}
                    </a>
                </li>
            `).join('');

        if (!suggestionsHTML) {
            this.phraseSuggestionsContainer.classList.add('voyado-search-form__results__container--hidden');
            return;
        }

        this.phraseSuggestionsContainer.classList.remove('voyado-search-form__results__container--hidden');
        this.phraseSuggestions.innerHTML = `<ul>${suggestionsHTML}</ul>`;
    }

    updateSubmitText() {
        this.searchSubmit.classList.toggle('hidden', !this.hasResults);

        if (!this.result) {
            return;
        }

        this.searchSubmit.innerText = `VIEW ALL ${this.result.totalHits} ITEMS`;
    }

    addProductClickListners() {
        for (const link of this.querySelectorAll<HTMLAnchorElement>('.voyado-search-form__product a')) {
            this.$listen(link, 'click', () => {
                const ticket = link.getAttribute('data-ticket');

                if (!ticket || !window.elevate) {
                    return;
                }

                resetElevateSessionIfTrackingConsentDenied();

                void window.elevate.notify.click(ticket);
            });
        }
    }
}
